/**
 * kamo bundle builder
 *
 * Renders a Loop Subscriptions BYOB bundle (box size -> flavor mix -> purchase
 * option) driven by Loop's public storefront bundle APIs, and on submit creates a
 * Loop bundle transaction + adds the bundle and Pfand lines to the cart.
 *
 * NO pricing rule is sourced from Loop -- neither the subscription discounts
 * (SUBSCRIPTION_OPTIONS) nor the one-time quantity tiers (ONE_TIME_TIERS); both
 * constants below carry the reasons. Loop supplies structure only: box sizes,
 * product list and base prices.
 */
(() => {
  if (customElements.get('kamo-bundle-builder')) return;

  const LOOP_STOREFRONT_BASE = 'https://api.loopsubscriptions.com/storefront/2026-04';

  // Pfand per 12-pack tray, keyed by product handle. See CLAUDE.md §3/§7.2.
  // variantId points at the dedicated Pfand product for that deposit rate/format
  // (verified live: "1x Pfand 12 x 0,25€" for cans, "Pfand 12 x 0.08" for bottles) --
  // the widget adds this as its own cart line at quantity = number of packs of that
  // format, per §7.2. Actual line price always comes from Shopify's own catalog at
  // cart time; perPackCents here is only for this widget's own price preview.
  const PFAND_CONFIG = {
    'kamo-mg': { variantId: 52184941756748, perPackCents: 300 }, // 12 cans x €0.25
    'kamo-45': { variantId: 52184941756748, perPackCents: 300 }, // 12 cans x €0.25
    'kamo-pro': { variantId: 58031604597068, perPackCents: 96 }, // 12 bottles x €0.08
  };

  // Marks the Pfand lines this widget adds, so reconcilePfand() only ever touches
  // its own lines. The store's existing Pfand automation (the "FeeBee fee" tagged
  // products, see docs/findings.md) uses these same variants for non-bundle orders --
  // reconciling those would destroy deposits we didn't create. Leading underscore
  // keeps it hidden from the customer in cart/checkout, and it is deliberately NOT
  // `_bundleId`: Loop groups on that key, and the Pfand line must stay outside the
  // bundle group (and outside its discount) per §7.2.
  const PFAND_MARKER = '_kamoPfand';

  // Subscription options and their pricing. NOT sourced from Loop's bundle API.
  //
  // Why: Loop's "SP for Bundle Only" selling plan bakes its 10%/15% discount into
  // the plan itself, applying to ANY line using that plan -- including the Pfand
  // line, which must legally never be discounted (§7). Shopify selling plans can't
  // exclude a specific product from their own price adjustment, and Loop's
  // bundle-level "Discounts" (which IS correctly scoped to _bundleId lines, as the
  // one-time path below proves) can't set a different % per selling-plan frequency.
  //
  // Fix: two new Shopify selling plans were created directly in Loop's admin
  // (Selling plans, not the bundle editor), each with discount type "None" (0%),
  // each linked to all 3 beer products AND both Pfand products:
  //   - id 691202097484, "Deliver every month"
  //   - id 691202130252, "Deliver every 3 months"
  // The 10%/15% now comes from two native Shopify discount codes instead, scoped
  // to only the 3 beer products (created with appliesOnSubscription: true,
  // appliesOnOneTimePurchase: false, recurringCycleLimit: 0 -- the simplified
  // Shopify discount-creation tooling defaults to one-time-only and silently
  // won't apply to subscription lines without that flag). Applying the code (see
  // applyDiscountCode) never touches the Pfand line, since it's product-scoped.
  // Because both the beer and Pfand lines share the exact same selling plan id,
  // Shopify groups them into the same subscription contract -- verified live for
  // both frequencies and both Pfand formats (can and bottle) before this was wired
  // into the widget.
  //
  // Loop's create-transaction endpoint does not validate sellingPlanShopifyId
  // against the plans actually linked to the bundle in Loop's own system (verified
  // live) -- it only matters for Loop's own bookkeeping/_bundleId grouping, so
  // using a plan Loop doesn't know about here is safe.
  // Both plans deliver and bill MONTHLY. They differ only in commitment length, which
  // is what earns the higher discount -- it rewards the longer commitment, not less
  // frequent shipping (a deliberate correction to the original brief, which had the
  // 15% tier shipping quarterly).
  //
  // Verified 2026-09-07: plan #691202130252 has "Minimum billing cycles: 3" set, under
  // **Advanced options** at the very bottom of the plan's page in Loop -- not in the
  // frequency block above it, which is where you look first and why this took two
  // passes to confirm. Loop's own hint reads "The initial order counts towards this
  // limit", so 3 cycles means the initial order plus two renewals, and the widget's
  // "3 month minimum term" label is accurate. If that setting is ever cleared, the
  // label becomes a false statement made during checkout and the 15% tier has to go.
  // Order here is display order: the 3-month commitment reads first because it is the
  // offer being made. Nothing is pre-selected -- see state.purchaseOption.
  // Every option is tiered by pack count, subscriptions included since 2026-09-08 --
  // before that the subscription discounts were flat, which meant the 24-can box
  // showed the same per-can price as the 12-can one and had nothing to recommend it.
  // Tiers are listed largest first; tierFor() picks the highest one the cart reaches.
  //
  //            1 pack   2 packs   3 packs
  //   one-time    0%       5%        8%
  //   monthly    10%      10%       15%
  //   3-month    15%      15%       20%
  //
  // Each tier has its own Shopify code, product-scoped to the 3 beers, and the
  // 3-pack codes additionally carry a minimum quantity of 3 so they are inert if
  // someone finds one and tries it on a smaller box.
  const SUBSCRIPTION_OPTIONS = [
    {
      id: 691202130252,
      label: 'every month · 3 month minimum term',
      tiers: [
        { minPacks: 3, discountPercent: 20, discountCode: 'BUNDLE3MONTH20' },
        { minPacks: 1, discountPercent: 15, discountCode: 'BUNDLE3MONTH15' },
      ],
    },
    {
      id: 691202097484,
      label: 'delivered every month',
      tiers: [
        { minPacks: 3, discountPercent: 15, discountCode: 'BUNDLEMONTHLY15' },
        { minPacks: 1, discountPercent: 10, discountCode: 'BUNDLEMONTHLY10' },
      ],
    },
  ];

  // One-time quantity tiers. Also NOT sourced from Loop's bundle API, for the same
  // reason the subscription options aren't -- and for one Loop taught us the hard way.
  //
  // These used to come from Loop's bundle-level "Discounts", which generated codes of
  // the form LPBNDL... . That worked for one-time carts, but Loop *owns* those codes:
  // when a customer edited their subscription in Loop's customer portal, Loop rebuilt
  // the bundle line from its own bundle definition and attached the one-time 5% code
  // to a subscription contract, alongside the 10% subscription code (Phase 5 case 6).
  // A discount Loop owns is a discount Loop will re-apply somewhere we didn't ask for.
  //
  // So Loop's bundle discounts are now set to 0% across the board and all discounting
  // belongs to us, via native Shopify codes scoped to the 3 beer products. Loop keeps
  // only what it is good at: which flavours, which box sizes.
  //
  // BUNDLEONETIME5 additionally carries a minimum quantity of 2 on those products, so
  // the code is inert if a customer finds it and tries it on a 12-can box.
  const ONE_TIME_TIERS = [
    { minPacks: 3, discountPercent: 8, discountCode: 'BUNDLEONETIME8' },
    { minPacks: 2, discountPercent: 5, discountCode: 'BUNDLEONETIME5' },
  ];

  // Money is handled in integer cents throughout to avoid binary floating-point
  // drift (e.g. 32.90 * 0.85 landing on 2796.4999999999995 instead of 2796.5).
  function toCents(amount) {
    return Math.round(amount * 100 + 1e-7);
  }

  function formatMoney(cents, currencySymbol) {
    const symbol = currencySymbol || '€';
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return `${symbol}${value}`;
  }

  // Reads a rendered money string back to cents, tolerating both separators: Loop
  // renders "€71.98" while the theme renders "€71,98 EUR". Returns null if the text
  // isn't a single money amount.
  function parseMoneyToCents(text) {
    const match = String(text).trim().match(/^[^\d-]*(\d{1,3}(?:[.,\s]\d{3})*[.,]\d{2})\s*[A-Z]{0,3}$/);
    if (!match) return null;
    const digits = match[1].replace(/[.,\s]/g, '');
    return Number.parseInt(digits, 10);
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  // Loop's bundle API returns each product's title with its variant title
  // appended, e.g. "IPA - magnesium beer. - 12x kamo MG" -- strip that repeated
  // pack-size suffix and show "IPA - magnesium beer × 12" instead.
  function shortProductName(product, variant) {
    let name = product.title || '';
    if (variant.title) {
      const escapedVariantTitle = variant.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      name = name.replace(new RegExp('\\s*-\\s*' + escapedVariantTitle + '$'), '');
    }
    name = name.replace(/\.\s*$/, '').trim();
    const qtyMatch = variant.title && variant.title.match(/(\d+)/);
    return qtyMatch ? `${name} × ${qtyMatch[1]}` : name;
  }

  // Selling plan id on a cart line, or null for a one-time line.
  function sellingPlanIdOf(line) {
    const allocation = line && line.selling_plan_allocation;
    return allocation && allocation.selling_plan ? allocation.selling_plan.id : null;
  }

  // Identity of a deposit line: which deposit, on which plan. Both halves matter --
  // see expectedPfandFromCart.
  function pfandKey(variantId, sellingPlanId) {
    return `${variantId}::${sellingPlanId || ''}`;
  }

  // Keeps the deposit lines this widget adds in step with the bundle lines that
  // justify them. Deliberately standalone: it runs on every page, not just the
  // product pages where <kamo-bundle-builder> exists, because the cart page and
  // cart drawer are exactly where a customer meets an orphaned deposit.
  class PfandReconciler {
    getCartUIElement() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    // --- Pfand reconciliation -------------------------------------------------
    //
    // The deposit lines we add are intentionally not part of Loop's bundle group
    // (no `_bundleId`), which means Loop's own cart UI can remove or edit a bundle
    // without knowing our Pfand lines exist -- leaving the customer paying a deposit
    // for beer that's no longer in the cart. This recomputes what the Pfand lines
    // *should* be from the bundle lines actually present, and corrects them.
    //
    // Only lines carrying PFAND_MARKER are ever touched, so deposits added by the
    // store's separate FeeBee automation for non-bundle orders are left alone.

    // Expected deposits, derived from the cart's real bundle lines.
    //
    // Keyed by variant *and* selling plan, not variant alone. A deposit has to sit on
    // the same plan as the beer that owes it: on a subscription so it recurs, on no
    // plan for a one-time box. Keying by variant alone missed the case where a
    // customer switches an existing cart from monthly to one-time -- the beer line is
    // replaced but the deposit line keeps its old plan, and a variant-only comparison
    // sees the right variant in the right quantity and leaves it there, producing a
    // cart of one-time beer with a deposit billed every month.
    //
    // With the plan in the key, that stale line simply isn't in `expected` any more:
    // it gets removed and a correctly-planned one added. The same key also keeps two
    // bundles on different plans from being conflated.
    //
    // A bundle line needs a deposit from us only if it doesn't already carry one.
    // `has_components` is the tell: the store's deposit app uses a Shopify Cart
    // Transform, so a line it covers arrives as a bundle (kamo 45 at €35,90 rather
    // than €32,90) that expands at checkout into the beer plus its deposit. Counting
    // those would charge the deposit twice.
    //
    // Deliberately keyed on that rather than on "is this a subscription": today the
    // transform happens to cover one-time lines and skip subscription ones, but that
    // is the deposit app's configuration, not a law. Reading the actual line means the
    // widget stays correct whichever way that config goes -- including the planned
    // change to exclude these 3 products from the transform, after which one-time
    // lines will start needing a deposit line from us and will simply get one.
    expectedPfandFromCart(cart) {
      const expected = {};
      cart.items.forEach((line) => {
        const isBundleLine = line.properties && line.properties._bundleId;
        if (!isBundleLine) return;
        if (line.has_components) return;
        const config = PFAND_CONFIG[line.handle];
        if (!config) return;
        const sellingPlan = sellingPlanIdOf(line);
        const key = pfandKey(config.variantId, sellingPlan);
        if (!expected[key]) expected[key] = { variantId: config.variantId, sellingPlan, quantity: 0 };
        expected[key].quantity += line.quantity;
      });
      return expected;
    }

    // The Pfand lines this widget owns, on the same variant+plan key.
    ownedPfandLines(cart) {
      const owned = {};
      cart.items.forEach((line) => {
        if (!line.properties || !line.properties[PFAND_MARKER]) return;
        owned[pfandKey(line.variant_id, sellingPlanIdOf(line))] = line;
      });
      return owned;
    }

    async reconcilePfand() {
      if (this.reconciling) return false;
      this.reconciling = true;

      try {
        const cart = await (await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })).json();
        const expected = this.expectedPfandFromCart(cart);
        const owned = this.ownedPfandLines(cart);

        // Any variant+plan combination either side knows about needs checking.
        const keys = new Set([...Object.keys(expected), ...Object.keys(owned)]);
        let changed = false;
        let lastResponse = null;

        // Shrink and remove before adding. A plan change shows up here as one key
        // going to zero and another appearing; doing the removal first means the
        // customer never sees a moment with two deposits for one beer.
        const ordered = [...keys].sort((a, b) => {
          const shrinks = (k) => ((expected[k] ? expected[k].quantity : 0) < (owned[k] ? owned[k].quantity : 0) ? 0 : 1);
          return shrinks(a) - shrinks(b);
        });

        for (const key of ordered) {
          const want = expected[key] ? expected[key].quantity : 0;
          const line = owned[key];
          const have = line ? line.quantity : 0;
          if (want === have) continue;

          if (line) {
            // Correct (or remove, when want is 0) an existing line. Addressed by
            // line key rather than index, since indexes shift as lines are removed.
            lastResponse = await this.changeCartLine(line.key, want);
          } else {
            // A deposit is owed with no line carrying it -- add one on the selling
            // plan the key names, so it lands in the same contract as its beer.
            lastResponse = await this.addPfandLine(expected[key].variantId, want, expected[key].sellingPlan);
          }
          changed = true;
        }

        if (changed) this.afterCartMutation(lastResponse, Object.keys(expected).length > 0);
        return changed;
      } catch (error) {
        // Reconciliation is best-effort: never break the page over it.
        console.warn('[kamo-bundle-builder] Pfand reconciliation failed', error);
        return false;
      } finally {
        this.reconciling = false;
      }
    }

    cartSectionsBody() {
      const cartUIElement = this.getCartUIElement();
      if (!cartUIElement) return {};
      return {
        sections: cartUIElement.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      };
    }

    async changeCartLine(key, quantity) {
      const response = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity, ...this.cartSectionsBody() }),
      });
      return response.json();
    }

    async addPfandLine(variantId, quantity, sellingPlan) {
      const item = { id: Number(variantId), quantity, properties: { [PFAND_MARKER]: '1' } };
      if (sellingPlan) item.selling_plan = sellingPlan;
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [item], ...this.cartSectionsBody() }),
      });
      return response.json();
    }

    // Re-render the theme's cart UI and let the rest of the theme know, without
    // re-opening the drawer if it wasn't already open.
    //
    // Only re-renders when no bundle lines remain. Re-rendering swaps out the DOM
    // nodes Loop's script observes, and Loop cannot re-populate its grouped bundle
    // row afterwards (see the note in onSubmit) -- so with a bundle still in the
    // cart we leave the markup alone and let the next page load render it properly.
    // The common reconciliation case is removing an orphaned deposit, where no
    // bundle line is left and there's nothing of Loop's to break.
    afterCartMutation(response, cartHasBundleLines) {
      const cartUIElement = this.getCartUIElement();
      const safeToRender = !cartHasBundleLines;
      if (
        safeToRender &&
        cartUIElement &&
        response &&
        response.sections &&
        typeof cartUIElement.renderContents === 'function'
      ) {
        const wasOpen = cartUIElement.hasAttribute('open') || cartUIElement.classList.contains('modal--open');
        cartUIElement.renderContents(response);
        if (!wasOpen && typeof cartUIElement.close === 'function') cartUIElement.close();
      }
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'kamo-bundle-builder-pfand-sync' });
      }
    }

    // Reconcile whenever the cart changes underneath us. Loop's own cart UI does
    // not publish the theme's cartUpdate event, so the drawer's contents are also
    // observed directly -- that's the path that actually orphans a Pfand line.
    watchCartForPfandDrift() {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source && event.source.startsWith('kamo-bundle-builder')) return;
          this.queueReconcile();
        });
      }

      const cartUIElement = this.getCartUIElement();
      const itemsContainer = cartUIElement && cartUIElement.querySelector('.js-contents');
      if (!itemsContainer) return;
      new MutationObserver(() => this.queueReconcile()).observe(itemsContainer, {
        childList: true,
        subtree: true,
      });
    }

    queueReconcile() {
      clearTimeout(this.reconcileTimer);
      this.reconcileTimer = setTimeout(() => this.reconcilePfand(), 300);
    }

    // --- Displayed cart total --------------------------------------------------
    //
    // Loop's script recalculates the cart total from its own bundle pricing and
    // writes it over the theme's "Estimated Total". It has no knowledge of the
    // native Shopify discount code we now use for subscriptions (see
    // SUBSCRIPTION_OPTIONS), so it shows the pre-discount figure -- a total that is
    // higher than what the customer will actually be charged.
    //
    // Verified: the server renders the correct value (€32,61 EUR in both the
    // "Subtotal" and "Estimated Total" rows); Loop overwrites only the latter,
    // afterwards and in its own formatting (dot decimal, no currency code).
    //
    // The neighbouring "Subtotal" row is left alone by Loop and Shopify renders it
    // from items_subtotal_price, so when that equals total_price -- which it does
    // whenever the discount lands as line-level allocations, i.e. our case -- it is
    // both correct and already formatted in the shop's own money format. Copy it
    // rather than trying to re-implement Shopify's money formatting. If the two
    // genuinely differ, leave the total alone rather than risk showing something
    // worse.
    async correctDisplayedTotal() {
      try {
        const totalEl = document.querySelector('.totals__subtotal-value span') ||
          document.querySelector('.totals__subtotal-value');
        const itemsSubtotalEl = document.querySelector('.totals__items-subtotal-value');
        if (!totalEl || !itemsSubtotalEl) return;

        const cart = await (await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })).json();
        if (cart.items_subtotal_price !== cart.total_price) return;

        const correct = itemsSubtotalEl.textContent.trim();
        if (!correct || totalEl.textContent.trim() === correct) return;
        totalEl.textContent = correct;
      } catch (error) {
        console.warn('[kamo-bundle-builder] could not correct displayed cart total', error);
      }
    }

    // Loop renders its grouped bundle row itself and prices it from its own bundle
    // model, so it shows the undiscounted line total (€71,98) while the cart summary
    // shows the real one (€63,12) -- the line items visibly don't add up to the total.
    // Shopify knows the correct figure (original_line_price vs final_line_price on the
    // `_bundleId` lines), so restate it the way the theme renders any other discounted
    // line: original struck through, discounted beside it.
    async correctLoopBundlePrices() {
      try {
        const containers = document.querySelectorAll(
          '.LOOP_BUNDLE_CONTAINER_CLASS, .LOOP_BUNDLE_DRAWER_CONTAINER_CLASS'
        );
        if (!containers.length) return;

        const cart = await (await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })).json();
        const bundleLines = cart.items.filter((line) => line.properties && line.properties._bundleId);
        if (!bundleLines.length) return;

        const original = bundleLines.reduce((sum, line) => sum + line.original_line_price, 0);
        const final = bundleLines.reduce((sum, line) => sum + line.final_line_price, 0);
        if (original === final) return;

        containers.forEach((container) => {
          // Loop's drawer markup prints the line price in three places -- its copy of
          // the theme's cart-table price columns, which stack vertically in a drawer
          // and read as three prices. Correct the first and hide the rest: rewriting
          // all three stacked three struck-through prices, and rewriting only the
          // first left two bare undiscounted ones, which is worse than either.
          if (container.querySelector('[data-kbb-price-fixed]')) return;
          let corrected = false;
          for (const node of container.querySelectorAll('*')) {
            // closest() rather than a flag on the node alone: the replacement markup
            // contains the original amount again inside an <s>, which would otherwise
            // match on the next pass and nest correction inside correction forever.
            if (node.children.length || node.closest('[data-kbb-price-fixed]')) continue;
            if (parseMoneyToCents(node.textContent) !== original) continue;
            if (!corrected) {
              node.dataset.kbbPriceFixed = '1';
              node.innerHTML =
                `<s>${escapeHtml(formatMoney(original))}</s> ${escapeHtml(formatMoney(final))}`;
              corrected = true;
            } else {
              // Same reason as the Edit control: `hidden` alone loses to any inline
              // or class-based display on Loop's nodes.
              node.hidden = true;
              node.style.setProperty('display', 'none', 'important');
            }
          }
        });
      } catch (error) {
        console.warn('[kamo-bundle-builder] could not correct the bundle line price', error);
      }
    }

    // Loop's bundle row spells the mix out one product at a time, in full catalogue
    // names, e.g. "IPA - magnesium beer. - 12x kamo MG x 1" on its own line per
    // flavour. Three lines of that plus a 75px image push the price and the Edit
    // control out of view on a phone. Condense to:
    //
    //   Your new beer routine.
    //   12× kamo MG | 12× kamo 45
    //   Monthly delivery
    //
    // A rewrite of someone else's markup, like correctLoopBundlePrices above, and
    // fragile in the same way: it recognises Loop's output by shape, so a change on
    // Loop's side makes it stop matching. It fails by leaving the original text alone,
    // which is the right direction to fail in.
    tidyLoopBundleRow() {
      const containers = document.querySelectorAll(
        '.LOOP_BUNDLE_CONTAINER_CLASS, .LOOP_BUNDLE_DRAWER_CONTAINER_CLASS'
      );
      containers.forEach((container) => {
        // Loop re-renders the container's contents on its own cart updates. The guard
        // therefore sits on the element we rewrote, not on the container -- a flag on
        // the container would survive Loop's re-render and skip the fresh markup.
        const flavourEls = [...container.querySelectorAll('.data-cart-item-selling-plan-name')];
        if (!flavourEls.length) return;
        if (flavourEls[0].dataset.kbbSummary === '1') return;

        // Nothing in Loop's cart row may lead to Loop's hosted bundle page: that page
        // is what this widget replaced, and a customer who lands there configures a
        // box at prices we no longer use. Bundles are built on the product pages.
        //
        // Matched by destination, not by class -- an earlier version keyed on the
        // theme classes Loop copies (.cart-item__name, .cart-item__link) and missed
        // the /cart page, where the title is a bare <a> with no class at all.
        //
        // Loop's own remove control is an <a> too, without an href, and its click
        // handler is the only way to empty a bundle -- explicitly spared.
        container.querySelectorAll('a').forEach((a) => {
          if (a.classList.contains('loop-remove-bundle-btn') || a.closest('.loop-remove-bundle-btn')) return;
          const href = a.getAttribute('href') || '';
          const leadsToLoop = href.includes('/loop_subscriptions/');
          const leadsNowhere = href === '';
          if (!leadsToLoop && !leadsNowhere) return;

          // `.cart-item__link` is the theme's invisible full-row overlay. Loop copies
          // it without the positioned wrapper that bounds it, so in the drawer it
          // resolved against .cart-drawer__inner and covered the whole panel
          // (measured 420x900) -- every click, Checkout included, went to Loop. It is
          // aria-hidden and tabindex="-1", so it can simply go.
          if (a.classList.contains('cart-item__link')) {
            a.remove();
            return;
          }

          // Everything else keeps its contents (the title text, or the product image)
          // and loses only the navigation.
          const span = document.createElement('span');
          span.className = a.className;
          if (a.getAttribute('style')) span.setAttribute('style', a.getAttribute('style'));
          while (a.firstChild) span.appendChild(a.firstChild);
          a.replaceWith(span);
        });

        // "<product> - <packSize>x <pack name> x <packs>" -> cans per pack name,
        // summed so two packs of one flavour read "24× kamo PRO" rather than twice.
        const cans = new Map();
        let parsedAll = true;
        flavourEls.forEach((el) => {
          const m = el.textContent.trim().match(/-\s*(\d+)\s*x\s*(.+?)\s*x\s*(\d+)\s*$/i);
          if (!m) { parsedAll = false; return; }
          const name = m[2].trim();
          cans.set(name, (cans.get(name) || 0) + Number(m[1]) * Number(m[3]));
        });
        if (!parsedAll || !cans.size) return;

        const summary = [...cans.entries()].map(([name, n]) => `${n}× ${name}`).join(' | ');
        const host = flavourEls[0].closest('p') ? flavourEls[0].closest('p').parentElement : null;
        if (!host) return;

        // Replace the per-flavour paragraphs with one line, and drop the <br> spacer
        // they were separated from the plan name by.
        flavourEls.forEach((el, i) => {
          const para = el.closest('p') || el;
          if (i === 0) {
            el.textContent = summary;
            el.dataset.kbbSummary = '1';
          } else {
            para.remove();
          }
        });
        host.querySelectorAll('br').forEach((br) => br.remove());

        // Plan names as Loop stores them are long; shorten the two we issue and leave
        // anything else untouched rather than guessing.
        const PLAN_LABELS = {
          'Deliver every month': 'Monthly delivery',
          'Deliver monthly, 3-month commitment': 'Monthly delivery · 3-month term',
        };
        host.querySelectorAll('p > span').forEach((span) => {
          const label = PLAN_LABELS[span.textContent.trim()];
          if (label) span.textContent = label;
        });

        // The bundle's own title. Renaming the bundle in Loop is the better fix -- it
        // would carry into the customer portal and Loop's emails too -- so this only
        // trims the current name and no-ops once that happens.
        container.querySelectorAll('a, span, div, p').forEach((node) => {
          if (node.children.length) return;
          if (/^your new beer routine starts here\.?$/i.test(node.textContent.trim())) {
            node.textContent = 'Your new beer routine.';
          }
        });

        // Loop's Edit control opens Loop's own hosted bundle page -- the builder this
        // widget replaced, showing different prices. Hidden rather than rerouted: an
        // attempt to point it at this widget could not remove the old cart lines
        // reliably and produced two bundles in one cart, which is a double charge
        // waiting to happen. See docs/findings.md. Removing the box and building a new
        // one is the supported path until real editing exists.
        //
        // Hidden with an inline `display: none`, not the `hidden` attribute: Loop's
        // button carries an inline `display: flex`, and an inline declaration beats
        // the attribute, so it stayed visible when only `hidden` was set. Our own
        // `[hidden] { display: none !important }` would have covered it, but that
        // stylesheet ships with the widget block and so isn't loaded on /cart.
        container.querySelectorAll('.loop-edit-bundle-btn').forEach((b) => {
          b.hidden = true;
          b.style.setProperty('display', 'none', 'important');
        });

        // Which makes Loop's remove control the only way to change a box -- and it
        // ships as a 14x15 icon with no label, well under any sensible tap target.
        // Given a word and 44px. Restyled in place, never replaced, so Loop's own
        // click handler survives.
        container.querySelectorAll('.loop-remove-bundle-btn').forEach((btn) => {
          if (btn.dataset.kbbLabelled === '1') return;
          btn.dataset.kbbLabelled = '1';
          btn.style.flexDirection = 'row';
          btn.style.alignItems = 'center';
          btn.style.gap = '6px';
          btn.style.height = 'auto';
          btn.style.minHeight = '44px';
          const label = document.createElement('span');
          label.textContent = 'remove';
          label.style.textDecoration = 'underline';
          btn.appendChild(label);
        });

        // 75px square down to the 56px the widget uses for the same product images --
        // and back into the document flow. The theme positions .cart-item__image out
        // of flow, which works inside its own .cart-item grid; Loop copies the class
        // without that grid, so the image resolved against .cart-drawer__inner and
        // sat in the drawer's top-left corner, over the header. Its container also
        // collapses to zero height for the same reason.
        container.querySelectorAll('.cart-item__image-container').forEach((box) => {
          box.style.position = 'static';
          box.style.height = 'auto';
          box.style.width = 'auto';
          box.style.flex = '0 0 auto';
        });
        container.querySelectorAll('img').forEach((img) => {
          img.style.position = 'static';
          img.style.width = '56px';
          img.style.height = '56px';
          img.style.objectFit = 'cover';
          img.style.borderRadius = '6px';
        });

      });
    }

    // Loop rewrites both the total and its bundle row asynchronously, and again after
    // its own cart updates, so watch and re-correct rather than fixing them once.
    watchDisplayedTotal() {
      const targets = [
        document.querySelector('.totals__wrapper') || document.querySelector('.js-contents-totals'),
        document.querySelector('.cart-drawer__cart-items') || document.querySelector('.js-contents'),
      ].filter(Boolean);
      if (!targets.length) return;

      const queue = () => {
        clearTimeout(this.totalTimer);
        this.totalTimer = setTimeout(() => {
          this.correctDisplayedTotal();
          this.correctLoopBundlePrices();
          this.tidyLoopBundleRow();
        }, 250);
      };
      const observer = new MutationObserver(queue);
      targets.forEach((t) => observer.observe(t, { childList: true, subtree: true, characterData: true }));
      queue();
    }
  }
  class KamoBundleBuilder extends HTMLElement {
    constructor() {
      super();

      this.bundleId = this.dataset.bundleId;
      this.shopDomain = this.dataset.shopDomain;
      this.showOneTime = this.dataset.showOneTime === 'true';
      this.currentProductHandle = (this.dataset.currentProduct || '').trim();
      this.sectionId = this.dataset.sectionId || 'kbb';

      this.state = {
        bundle: null,
        boxSizeId: null,
        quantities: {}, // { [variantId]: qty }
        purchaseOption: null, // 'one_time' | sellingPlan.shopifyId (number)
      };

      this.overrides = {};

      this.loadingEl = this.querySelector('[data-kbb-loading]');
      this.errorEl = this.querySelector('[data-kbb-error]');
      this.contentEl = this.querySelector('[data-kbb-content]');
      this.boxSizeOptionsEl = this.querySelector('[data-kbb-box-size-options]');
      this.progressEl = this.querySelector('[data-kbb-progress]');
      this.flavorsEl = this.querySelector('[data-kbb-flavors]');
      this.purchaseOptionCardsEl = this.querySelector('[data-kbb-purchase-option-cards]');
      this.totalEl = this.querySelector('[data-kbb-total]');
      this.pfandEl = this.querySelector('[data-kbb-pfand]');
      this.ctaEl = this.querySelector('[data-kbb-cta]');
      this.ctaErrorEl = this.querySelector('[data-kbb-cta-error]');
      this.minusIconTemplate = this.querySelector('[data-kbb-icon-minus]');
      this.plusIconTemplate = this.querySelector('[data-kbb-icon-plus]');

      this.ctaEl.addEventListener('click', this.onSubmit.bind(this));
    }

    connectedCallback() {
      this.overrides = this.readOverrides();
      this.init();
    }

    readOverrides() {
      const map = {};
      this.querySelectorAll('[data-override]').forEach((el) => {
        map[el.dataset.productId] = {
          displayName: el.dataset.displayName || '',
          tagline: el.dataset.tagline || '',
        };
      });
      return map;
    }

    async init() {
      if (!this.bundleId) {
        this.showError('no loop bundle configured.');
        return;
      }

      try {
        const url = `${LOOP_STOREFRONT_BASE}/bundle/${encodeURIComponent(this.bundleId)}?myshopifyDomain=${encodeURIComponent(this.shopDomain)}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const json = await response.json();

        if (!response.ok || !json.success) {
          throw new Error((json && json.message) || 'could not load the bundle.');
        }

        const bundle = json.data;

        // Defensive: the bundle's product list should only ever be the 3 beers, but
        // Loop admin misconfiguration (a Pfand product accidentally linked to the
        // bundle's selling plan) has been observed to make Loop's own API return a
        // Pfand product as a bundle product too. Never let a Pfand product render as
        // a selectable flavor -- it's identified by variant id against PFAND_CONFIG,
        // not by trusting whatever this response happens to include.
        const pfandVariantIds = new Set(Object.values(PFAND_CONFIG).map((c) => c.variantId));

        // Products with no variant at all can't be rendered; out-of-stock variants
        // are kept but greyed out in renderFlavors() rather than removed, so the
        // customer can still see the full lineup.
        bundle.products = bundle.products
          .filter((product) => product.variants.length > 0)
          .filter((product) => !pfandVariantIds.has(product.variants[0].shopifyId));

        if (!bundle.boxSizes || bundle.boxSizes.length === 0 || bundle.products.length === 0) {
          throw new Error('this bundle is currently unavailable.');
        }

        // The flavour whose product page this is goes first and starts pre-filled.
        // A customer on the kamo 45 page has already made that choice once; asking
        // again is friction, and on the 12-can box it means the CTA is live on arrival.
        if (this.currentProductHandle) {
          const i = bundle.products.findIndex((p) => p.handle === this.currentProductHandle);
          if (i > 0) bundle.products.unshift(bundle.products.splice(i, 1)[0]);
        }

        this.state.bundle = bundle;
        const defaultBoxSize = bundle.boxSizes.find((box) => box.defaultSelected) || bundle.boxSizes[0];
        this.state.boxSizeId = defaultBoxSize.id;
        this.preselectCurrentProduct();

        // Deliberately nothing pre-selected. A pre-ticked subscription -- and a
        // 3-month commitment in particular -- decides a recurring payment for the
        // customer instead of letting them choose it, which is what EU consumer law
        // is unhappy about. Order draws attention; the tick would be the decision.
        this.state.purchaseOption = null;

        this.render();
        this.loadingEl.hidden = true;
        this.contentEl.hidden = false;

        // Catch carts left inconsistent by a previous session (e.g. a bundle
        // removed via Loop's cart UI or its customer portal), then keep watching.
        // Reconciliation itself is bootstrapped globally (see the bottom of this
        // file) so it also runs on the cart page, where the widget isn't rendered.
        // Nudge it here too, now that a fresh add-to-cart is possible.
        pfandReconciler.reconcilePfand();
      } catch (error) {
        this.showError(error.message);
      }
    }

    showError(message) {
      this.loadingEl.hidden = true;
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    get currentBoxSize() {
      return this.state.bundle.boxSizes.find((box) => box.id === this.state.boxSizeId);
    }

    // One pack of the page's own flavour, if there's room for it and it's in stock.
    // Never more than that: filling a 24-can box entirely would take the choice away
    // rather than save a step.
    preselectCurrentProduct() {
      if (!this.currentProductHandle || !this.state.bundle) return;
      const product = this.state.bundle.products.find((p) => p.handle === this.currentProductHandle);
      if (!product) return;
      const variant = product.variants[0];
      if (!variant || variant.outOfStock) return;
      if (this.targetPacks() < 1) return;
      this.state.quantities[String(variant.shopifyId)] = 1;
    }

    targetPacks() {
      const boxSize = this.currentBoxSize;
      return boxSize ? boxSize.minSize : 0;
    }

    totalPacks() {
      return Object.values(this.state.quantities).reduce((sum, qty) => sum + qty, 0);
    }

    // All money getters below return integer cents.
    pfandTotal() {
      const bundle = this.state.bundle;
      let total = 0;
      bundle.products.forEach((product) => {
        const variant = product.variants[0];
        const qty = this.state.quantities[String(variant.shopifyId)] || 0;
        const config = PFAND_CONFIG[product.handle];
        total += config ? config.perPackCents * qty : 0;
      });
      return total;
    }

    // Packs per Pfand variant id, e.g. { 52184941756748: 2 } -- one dedicated cart
    // line per distinct Pfand product, quantity = total packs of that format.
    pfandLineQuantities() {
      const bundle = this.state.bundle;
      const byVariant = {};
      bundle.products.forEach((product) => {
        const variant = product.variants[0];
        const qty = this.state.quantities[String(variant.shopifyId)] || 0;
        const config = PFAND_CONFIG[product.handle];
        if (!config || qty <= 0) return;
        byVariant[config.variantId] = (byVariant[config.variantId] || 0) + qty;
      });
      return byVariant;
    }

    subtotal() {
      const bundle = this.state.bundle;
      let total = 0;
      bundle.products.forEach((product) => {
        const variant = product.variants[0];
        const qty = this.state.quantities[String(variant.shopifyId)] || 0;
        total += toCents(parseFloat(variant.price)) * qty;
      });
      return total;
    }

    applyDiscount(cents, percent) {
      return Math.round(cents * (1 - percent / 100) + 1e-7);
    }

    // What the customer will actually be charged for the beer, computed the way
    // Shopify computes it rather than the way arithmetic suggests.
    //
    // Shopify allocates a percentage code per cart line and truncates each allocation
    // to whole cents -- observed across several live orders: €32,90 at 5% allocates
    // €1,64 (not €1,65, though 164.5 rounds up), €35,99 allocates €1,79, €35,90
    // allocates €1,79, and at 10% €32,90 allocates €3,29. Discounting the summed
    // subtotal instead gave a preview one cent below the checkout (€69,41 vs €69,42),
    // which is small but sits on two screens the same customer reads in sequence.
    //
    // One cart line per flavour, so the truncation happens per flavour, not per pack.
    discountedSubtotal(percent) {
      const bundle = this.state.bundle;
      let total = 0;
      bundle.products.forEach((product) => {
        const variant = product.variants[0];
        const qty = this.state.quantities[String(variant.shopifyId)] || 0;
        if (!qty) return;
        const lineCents = toCents(parseFloat(variant.price)) * qty;
        total += lineCents - Math.floor((lineCents * percent) / 100 + 1e-7);
      });
      return total;
    }

    // The tier an option earns at a given pack count, or null if it earns nothing
    // (a 1-pack one-time box). One resolver for one-time and subscriptions alike --
    // they used to be handled separately, which is how the subscription options
    // ended up ignoring the box size.
    tierFor(optionId, packs) {
      if (!optionId) return null;
      const total = packs === undefined ? this.totalPacks() : packs;
      const option = SUBSCRIPTION_OPTIONS.find((o) => o.id === optionId);
      const tiers = optionId === 'one_time' ? ONE_TIME_TIERS : (option && option.tiers) || [];
      const eligible = tiers
        .filter((t) => t.minPacks <= total)
        .sort((a, b) => b.minPacks - a.minPacks);
      return eligible.length ? eligible[0] : null;
    }

    discountForOption(optionId, packs) {
      const tier = this.tierFor(optionId, packs);
      return tier ? tier.discountPercent : 0;
    }

    render() {
      this.renderBoxSizes();
      this.renderProgress();
      this.renderFlavors();
      this.renderPurchaseOptions();
      this.renderSummary();
    }

    renderBoxSizes() {
      const bundle = this.state.bundle;
      this.boxSizeOptionsEl.innerHTML = '';

      bundle.boxSizes.forEach((box) => {
        const packs = box.minSize;
        const cans = packs * 12;
        const perCanCents = this.perCanCentsForBoxSize(packs, cans);

        const label = document.createElement('label');
        label.className = 'kbb__box-size-option';
        label.innerHTML = `
          <input type="radio" name="kbb-box-size-${this.sectionId}" value="${escapeHtml(box.id)}" ${box.id === this.state.boxSizeId ? 'checked' : ''}>
          <span class="kbb__box-size-card">
            <span class="kbb__box-size-name">${escapeHtml(box.name)}</span>
            <span class="kbb__box-size-per-can">${formatMoney(perCanCents, bundle.currencySymbol)}/can</span>
          </span>
        `;
        label.querySelector('input').addEventListener('change', () => {
          this.state.boxSizeId = box.id;
          this.state.quantities = {};
          this.preselectCurrentProduct();
          this.render();
        });
        this.boxSizeOptionsEl.appendChild(label);
      });
    }

    // Pack price (cents) the box-size cards should quote, before any discount.
    //
    // Whatever is in the box, averaged -- so on the kamo PRO page the card reads
    // €2,99/can, the price of the thing actually selected, and it moves as the mix
    // changes. It used to be the cheapest flavour in the bundle regardless of page,
    // which on the PRO page advertised €2,74: a price reachable only by removing the
    // beer the customer had just come to look at.
    //
    // Empty box (both counts at zero, possible after a manual clear) falls back to
    // this page's own flavour, and failing that to the cheapest.
    referencePackPriceCents() {
      const packs = this.totalPacks();
      if (packs > 0) return Math.round(this.subtotal() / packs);
      const bundle = this.state.bundle;
      if (this.currentProductHandle) {
        const own = bundle.products.find((p) => p.handle === this.currentProductHandle);
        if (own && own.variants[0]) return toCents(parseFloat(own.variants[0].price));
      }
      return this.lowestPackPriceCents();
    }

    // Cheapest pack price (cents) before any discount, for the box toggle's "from"
    // price. The box size is chosen before the mix is, so there's no exact price yet.
    //
    // Deliberately the lowest rather than the average: an average (€2,83 here) is a
    // price no customer can actually pay, and it read as *higher* than the €2,74 the
    // product page itself shows above the widget on the two cheaper flavours. A
    // "from" price off the cheapest flavour never overstates and lines up with that.
    lowestPackPriceCents() {
      const bundle = this.state.bundle;
      if (!bundle.products.length) return 0;
      return Math.min(...bundle.products.map((product) => toCents(parseFloat(product.variants[0].price))));
    }

    // Effective price per can for a box size, per CLAUDE.md §1 ("each showing
    // effective price per can").
    //
    // This has to apply the discount that box size would actually earn, otherwise the
    // packs cancel out of (packPrice x packs) / (packs x 12) and both box sizes always
    // show the identical figure -- which is what they did, hiding the fact that 24
    // cans are cheaper per can on a one-time purchase.
    //
    // Which discount applies depends on the selected purchase option: the one-time
    // tiers are quantity-based (0% at 1 pack, 5% at 2), so they differ per box size;
    // the subscription discounts are flat, so both box sizes legitimately show the
    // same per-can price under those.
    perCanCentsForBoxSize(packs, cans) {
      if (!cans) return 0;
      // With no purchase option chosen yet, quote the one-time tier for that box size.
      // Otherwise the 24-can box would advertise the same per-can price as the 12-can
      // one and lose the very thing that makes it worth choosing. The one-time tier is
      // also the conservative figure: it never promises a subscription discount to a
      // customer who has not picked a subscription.
      // `packs` is the card's own box size, not the selected one, so each card quotes
      // the tier that box size would actually earn.
      const discountPercent = this.discountForOption(this.state.purchaseOption || 'one_time', packs);
      const packPrice = this.applyDiscount(this.referencePackPriceCents(), discountPercent);
      // Truncated, not rounded, to match how the theme's own product cards render a
      // per-unit price (kamo PRO shows €2,99/330ml, not €3,00) and because a per-unit
      // figure should never read higher than the price actually charged.
      return Math.floor((packPrice * packs) / cans);
    }

    renderProgress() {
      const target = this.targetPacks();
      const total = this.totalPacks();
      const complete = total === target;
      this.progressEl.innerHTML = `
        <span class="kbb__progress-track"><span class="kbb__progress-fill" data-complete="${complete}" style="width:${target ? Math.min(100, (total / target) * 100) : 0}%"></span></span>
        <span class="kbb__progress-count">${total} / ${target}</span>
      `;
    }

    renderFlavors() {
      const bundle = this.state.bundle;
      const target = this.targetPacks();
      const total = this.totalPacks();
      this.flavorsEl.innerHTML = '';

      bundle.products.forEach((product) => {
        const variant = product.variants[0];
        const key = String(variant.shopifyId);
        if (!(key in this.state.quantities)) this.state.quantities[key] = 0;
        const qty = this.state.quantities[key];
        const soldOut = !!variant.outOfStock;
        const override = this.overrides[String(product.shopifyId)] || {};
        const displayName = override.displayName || shortProductName(product, variant);
        const tagline = soldOut ? 'currently sold out' : override.tagline || '';

        // The highlight follows what is *in the box*, not which page you're on: a
        // flavour lights up when its count goes above zero and goes plain again at
        // zero. The page's own flavour therefore arrives highlighted because it
        // arrives pre-filled, not as a separate rule. --current stays as a marker for
        // which row that is, without styling of its own.
        const isCurrent = !!this.currentProductHandle && product.handle === this.currentProductHandle;
        const row = document.createElement('div');
        row.className = ['kbb__flavor-row', isCurrent ? 'kbb__flavor-row--current' : '', qty > 0 ? 'kbb__flavor-row--selected' : '']
          .filter(Boolean)
          .join(' ');
        row.dataset.disabled = String(soldOut);
        row.innerHTML = `
          <img class="kbb__flavor-image" src="${escapeHtml(variant.imageURL)}" alt="" loading="lazy" width="56" height="56">
          <div class="kbb__flavor-info">
            <p class="kbb__flavor-name">${escapeHtml(displayName)}</p>
            ${tagline ? `<p class="kbb__flavor-tagline">${escapeHtml(tagline)}</p>` : ''}
          </div>
          <div class="kbb__stepper">
            <button type="button" class="kbb__stepper-btn" data-action="decrease" aria-label="decrease ${escapeHtml(displayName)}" ${soldOut || qty <= 0 ? 'disabled' : ''}></button>
            <span class="kbb__stepper-value" aria-live="polite">${qty}</span>
            <button type="button" class="kbb__stepper-btn" data-action="increase" aria-label="increase ${escapeHtml(displayName)}" ${soldOut || total >= target ? 'disabled' : ''}></button>
          </div>
        `;

        const decreaseBtn = row.querySelector('[data-action="decrease"]');
        const increaseBtn = row.querySelector('[data-action="increase"]');
        if (this.minusIconTemplate) decreaseBtn.appendChild(this.minusIconTemplate.content.cloneNode(true));
        if (this.plusIconTemplate) increaseBtn.appendChild(this.plusIconTemplate.content.cloneNode(true));

        if (!soldOut) {
          decreaseBtn.addEventListener('click', () => this.changeQty(key, -1));
          increaseBtn.addEventListener('click', () => this.changeQty(key, 1));
        }

        this.flavorsEl.appendChild(row);
      });
    }

    changeQty(variantKey, delta) {
      const current = this.state.quantities[variantKey] || 0;
      const target = this.targetPacks();
      const total = this.totalPacks();
      if (delta > 0 && total >= target) return;
      if (delta < 0 && current <= 0) return;
      this.state.quantities[variantKey] = current + delta;
      this.render();
    }

    renderPurchaseOptions() {
      const bundle = this.state.bundle;
      // Subscriptions first, one-time last: the subscription is the offer being made,
      // and the order of the cards is what a customer reads first. Which option is
      // *selected* on arrival is a separate question -- still one-time, deliberately.
      const options = SUBSCRIPTION_OPTIONS.map((option) => ({ id: option.id, name: option.label }));
      if (this.showOneTime) options.push({ id: 'one_time', name: 'one-time purchase' });

      this.purchaseOptionCardsEl.innerHTML = '';

      options.forEach((option) => {
        const discountPercent = this.discountForOption(option.id);
        const subtotal = this.subtotal();
        const discounted = this.discountedSubtotal(discountPercent);

        const wrapper = document.createElement('label');
        wrapper.className = 'kbb__purchase-option';
        wrapper.innerHTML = `
          <input type="radio" name="kbb-purchase-option-${this.sectionId}" value="${option.id}" ${this.state.purchaseOption === option.id ? 'checked' : ''}>
          <span class="kbb__purchase-option-card">
            <span class="kbb__purchase-option-label">
              <span class="kbb__purchase-option-name">${escapeHtml(option.name)}</span>
              ${discountPercent > 0 ? `<span class="kbb__purchase-option-badge">save ${discountPercent}%</span>` : ''}
            </span>
            <span class="kbb__purchase-option-prices">
              ${discountPercent > 0 ? `<span class="kbb__purchase-option-compare">${formatMoney(subtotal, bundle.currencySymbol)}</span>` : ''}
              <span class="kbb__purchase-option-price">${formatMoney(discounted, bundle.currencySymbol)}</span>
            </span>
          </span>
        `;
        wrapper.querySelector('input').addEventListener('change', () => {
          this.state.purchaseOption = option.id;
          this.render();
        });
        this.purchaseOptionCardsEl.appendChild(wrapper);
      });
    }

    renderSummary() {
      const bundle = this.state.bundle;
      const target = this.targetPacks();
      const total = this.totalPacks();
      const complete = total === target && target > 0;
      const discountPercent = this.discountForOption(this.state.purchaseOption);
      const discounted = this.discountedSubtotal(discountPercent);
      const pfand = this.pfandTotal();

      this.totalEl.innerHTML = `<span>total</span><span>${formatMoney(discounted, bundle.currencySymbol)}</span>`;
      this.pfandEl.textContent = pfand > 0 ? `+ ${formatMoney(pfand, bundle.currencySymbol)} deposit` : '';

      // A disabled button has to say why. Two things can be missing, and the label
      // names whichever one comes first: fill the box, then choose a frequency.
      const chosen = !!this.state.purchaseOption;
      const remaining = target - total;
      this.ctaEl.disabled = !complete || !chosen;
      if (remaining > 0) {
        this.ctaEl.textContent = `choose ${remaining} more pack${remaining === 1 ? '' : 's'}`;
      } else if (!chosen) {
        this.ctaEl.textContent = 'choose how often';
      } else {
        this.ctaEl.textContent = 'add to cart';
      }
      this.ctaErrorEl.hidden = true;
    }

    setSubmitting(submitting) {
      this.ctaEl.disabled = submitting || this.totalPacks() !== this.targetPacks() || !this.state.purchaseOption;
      this.ctaEl.setAttribute('aria-busy', submitting ? 'true' : 'false');
      this.ctaEl.textContent = submitting ? 'one moment …' : 'add to cart';
    }

    showCtaError(message) {
      this.ctaErrorEl.hidden = false;
      this.ctaErrorEl.textContent = message;
    }

    getCartUIElement() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    // No discountId is sent: Loop's bundle discounts are 0% and all discounting is
    // ours now (see ONE_TIME_TIERS). Omitting it is the path a 1-pack one-time box
    // already took, so it is the proven shape, not a new one.
    async createBundleTransaction() {
      const isOneTime = this.state.purchaseOption === 'one_time';
      const body = {
        boxSizeId: this.state.boxSizeId,
        sellingPlanShopifyId: isOneTime ? null : this.state.purchaseOption,
        quantity: 1,
        productVariants: Object.entries(this.state.quantities)
          .filter(([, qty]) => qty > 0)
          .map(([shopifyId, quantity]) => ({ shopifyId: Number(shopifyId), quantity })),
      };

      const url = `${LOOP_STOREFRONT_BASE}/bundle/${encodeURIComponent(this.state.bundle.id)}/transaction?myshopifyDomain=${encodeURIComponent(this.shopDomain)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw new Error((json && json.message) || 'could not add the bundle to your cart.');
      }
      return json.data.txnId;
    }

    // Sets the cart's discount code, or clears it when passed an empty string.
    //
    // Uses /cart/update.js rather than the /discount/{code} redirect: that route
    // *accumulates* codes in the cart session rather than replacing them, so a
    // customer who configured a 3-month box and then switched to monthly kept the
    // richer BUNDLE3MONTH15 alongside the new BUNDLEMONTHLY10 -- and Shopify applied
    // the 15% one. This route replaces the whole set (verified live), which also
    // gives us a way to clear a stale code when the current selection earns none.
    //
    // Must run BEFORE /cart/add.js so the section HTML that call returns already
    // reflects the discounted total. Applying to an empty cart is fine: the code is
    // held (marked not-applicable) and takes effect as soon as the items land.
    async applyDiscountCode(code) {
      await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: code || '' }),
      });
    }

    buildCartItems(txnId) {
      const isOneTime = this.state.purchaseOption === 'one_time';
      const sellingPlan = isOneTime ? undefined : this.state.purchaseOption;

      const bundleItems = Object.entries(this.state.quantities)
        .filter(([, qty]) => qty > 0)
        .map(([variantId, quantity]) => {
          const item = {
            id: Number(variantId),
            quantity,
            properties: { _bundleId: txnId },
          };
          if (sellingPlan) item.selling_plan = sellingPlan;
          return item;
        });

      // Deposits are added here only for subscriptions; one-time is left to
      // reconcilePfand() once the cart comes back.
      //
      // Whether a beer line needs a deposit from us depends on `has_components`, which
      // only exists after the line is in the cart -- the store's deposit app applies
      // its Cart Transform server-side, so at this point we cannot yet tell whether a
      // one-time line will arrive already carrying its deposit (today it does: €35,90
      // rather than €32,90, expanding at checkout into beer + deposit). Adding one
      // here regardless charged it twice -- €38,90 for a box that should cost €35,90.
      //
      // Subscription lines are safe to decide upfront: the transform has never applied
      // to them (they stay at €32,90, `has_components: false`, no deposit anywhere,
      // which is the §7.1 gap this widget exists to close and why the pre-existing
      // subscription order #2740 carried none). Adding them here rather than waiting
      // for reconciliation keeps the deposit and its beer in the same cart request.
      const pfandItems = isOneTime
        ? []
        : Object.entries(this.pfandLineQuantities()).map(([variantId, quantity]) => {
            const item = { id: Number(variantId), quantity, properties: { [PFAND_MARKER]: '1' } };
            // Safe to share the selling plan: it's a 0%-discount plan (see
            // SUBSCRIPTION_OPTIONS above), so the Pfand line stays undiscounted while
            // still landing in the same subscription contract as the bundle items.
            item.selling_plan = sellingPlan;
            return item;
          });

      return bundleItems.concat(pfandItems);
    }


    async addToCart(items) {
      const cartUIElement = this.getCartUIElement();
      const body = { items };
      if (cartUIElement) {
        body.sections = cartUIElement.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      // Matches this theme's own house fetchConfig() convention (assets/global.js):
      // POST to routes.cart_add_url (no .js suffix) with Accept: application/javascript
      // -- a plain application/json Accept header gets a generic "Cart Error" here.
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      });
      const json = await response.json();
      if (!response.ok || json.status) {
        throw new Error((json && (json.description || json.message)) || 'could not update your cart.');
      }
      return { response: json, cartUIElement };
    }

    async onSubmit() {
      const target = this.targetPacks();
      const total = this.totalPacks();
      if (total !== target || !this.state.purchaseOption || this.ctaEl.disabled) return;

      this.ctaErrorEl.hidden = true;
      this.setSubmitting(true);

      try {
        const isOneTime = this.state.purchaseOption === 'one_time';

        // Always set the code explicitly -- including clearing it when this selection
        // earns no discount (a 1-pack one-time box), so a code left over from a
        // previous configuration can't silently carry over.
        const tier = this.tierFor(this.state.purchaseOption);
        await this.applyDiscountCode(tier && tier.discountPercent > 0 ? tier.discountCode : '');

        const txnId = await this.createBundleTransaction();
        const items = this.buildCartItems(txnId);
        const { response, cartUIElement } = await this.addToCart(items);

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'kamo-bundle-builder' });
        }

        // Navigate rather than re-rendering the drawer in place.
        //
        // Loop's Liquid deliberately skips every `_bundleId` line in the theme's cart
        // loop and leaves an empty LOOP_BUNDLE_DRAWER_CONTAINER_CLASS for its own
        // script to fill with the grouped bundle row. That script binds its observers
        // once, at page load. Calling renderContents() swaps those DOM nodes out, so
        // Loop's observers end up watching detached elements and never re-render --
        // leaving a cart that shows the Pfand lines but *none of the beer*. Verified:
        // re-running setupMutationLoop(), setupMutationOldLoop(), getCartItemsLoop()
        // and even re-injecting loop_bundle.js all fail to repopulate it; only a real
        // page load does. Loop's own bundle config sets redirectionUrl to /cart for
        // exactly this reason.
        window.location = this.state.bundle.redirectionUrl || window.routes.cart_url || '/cart';
        return;
      } catch (error) {
        this.showCtaError(error.message);
      } finally {
        this.setSubmitting(false);
      }
    }
  }

  // Declared before customElements.define, since defining the element can run
  // connectedCallback synchronously for elements already in the DOM.
  //
  // Runs on every page this script is loaded on, including /cart and any page with
  // the cart drawer -- not just the product pages that render the widget. A deposit
  // orphaned by an edit in Loop's cart UI is most likely to be noticed on the cart
  // page, which is precisely where the widget doesn't exist.
  const pfandReconciler = new PfandReconciler();

  customElements.define('kamo-bundle-builder', KamoBundleBuilder);

  function bootstrapPfandReconciler() {
    pfandReconciler.reconcilePfand();
    pfandReconciler.watchCartForPfandDrift();
    pfandReconciler.watchDisplayedTotal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapPfandReconciler);
  } else {
    bootstrapPfandReconciler();
  }
})();
